import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { DraftOutbox } from '../repositories/drafts/draftOutbox';

/**
 * Have the browser warn before the page closes while any draft is unsynced
 * or a removal is unfinished: on the web they live only in this page
 * (FR-007a). The handler is registered only while there is something to
 * lose, and checks again as the page unloads, so a sign-out that just
 * discarded everything leaves without a warning (FR-009d).
 */
export function useBeforeUnloadWhileUnsynced(outbox: DraftOutbox | null): void {
  const subscribe = useCallback(
    (onChange: () => void) => outbox?.subscribe(onChange) ?? (() => {}),
    [outbox],
  );
  const pending = useSyncExternalStore(subscribe, () => outbox?.hasPendingWork() ?? false);

  useEffect(() => {
    if (!pending || !outbox) return;
    const warn = (event: BeforeUnloadEvent) => {
      if (!outbox.hasPendingWork()) return;
      event.preventDefault();
      // Older browsers show the prompt only when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending, outbox]);
}
